import {Component, input, OnDestroy, OnInit} from '@angular/core';
import {DjangoApiService} from "../../../shared/services/django-api.service";
import {MatDialog} from "@angular/material/dialog";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {DatePipe, NgIf} from "@angular/common";
import {PriceData} from "../../../core/interfaces/price.interface";
import {CardsBasicComponent} from "../../../shared/components/cards/cards-basic/cards-basic.component";
import {FormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatError, MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {provideNativeDateAdapter} from "@angular/material/core";
import {TableBasicComponent} from "../../../shared/components/tables/table-basic/table-basic.component";
import {forkJoin, Subscription} from "rxjs";
import {PageEvent} from "@angular/material/paginator";
import {LangChangeEvent, TranslateService} from "@ngx-translate/core";
import {LanguageService} from "../../../shared/services/language.service";
import {ActivatedRoute} from "@angular/router";


@Component({
  selector: 'app-price-admin',
  standalone: true,
  imports: [
    CardsBasicComponent,
    FormsModule,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    NgIf,
    TableBasicComponent
  ],
  providers: [provideNativeDateAdapter(), DatePipe],
  templateUrl: './price-admin.component.html',
  styleUrl: './price-admin.component.css'
})
export class PriceAdminComponent implements OnInit, OnDestroy {
  productData = input<any>();
  productId!: any;
  priceListData!: any;
  isFormVisible: boolean = false;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  selectedRows: any[] = [];
  pageSize: number = 20;
  currentPage: number = 1;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  private subscriptions: Subscription = new Subscription();

  price: PriceData = {
    close: '',
    date: '',
    product: ''
  };

  columnDef = [
    {key: 'date', header: 'Date'},
    {key: 'close', header: 'Closing Price'},
  ];

  constructor(
    private apiService: DjangoApiService,
    public dialog: MatDialog,
    private snackbarService: SnackbarService,
    private datePipe: DatePipe,
    public _languageService: LanguageService,
    private _translate: TranslateService,
    private _route: ActivatedRoute) {
  }

  ngOnInit() {
    this.setupLanguageSubscription();
    this.getRouteParams();
  }

  getRouteParams() {
    this.subscriptions.add(
      this._route.params.subscribe(params => {
        this.productId = this._route.snapshot.parent?.params['productId'];

        this.loadPriceListDate()
      })
    );
  }

  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }


  loadPriceListDate() {
    this.subscriptions.add(
      this.apiService.fetchPriceList(this.productId, this.currentPage, this.pageSize).subscribe({
        next: (res: any) => {
          this.priceListData = res;
        },
        error: (error) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  toggleFormVisibility() {
    this.isFormVisible = !this.isFormVisible;
    this.resetFormData();
  }

  private resetFormData(): void {
    this.price = {
      close: '',
      date: '',
      product: ''
    };
    this.isEditMode = false;
  }

  private formatDate(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
  }

  savePrice() {
    if (this.isEditMode) {
    } else {
      this.createPrice();
    }
  }

  createPrice() {
    this.isLoading = true;
    const formattedPrice = {
      close: this.price.close,
      date: this.formatDate(new Date(this.price.date!)),
      product: this.productId
    };
    this.apiService.createPrice(formattedPrice).subscribe({
      next: (res: any) => {
        this.isFormVisible = false;
        this.snackbarService.show(res.message);
        this.isLoading = false;
        this.loadPriceListDate();
      },
      error: (error) => {
        console.error('There was an error creating the product', error);
        this.isLoading = false;
      }
    });
  }

  deleteSelectedItems() {
    this.isLoading = true;
    const deleteObservables = this.selectedRows.map(row =>
      this.apiService.deletePrice(row.id)
    );

    forkJoin(deleteObservables).subscribe({
      next: (responses: any[]) => {
        responses.forEach(res => this.snackbarService.show(res.message));
        if (this.priceListData.results.length == this.selectedRows.length) {
          this.currentPage = this.currentPage - 1;
        }
        this.loadPriceListDate();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('There was an error deleting the prices', error);
      }
    });
  }

  deleteAllPrices() {
    this.apiService.deleteAllPrice(this.productId).subscribe({
      next: (res: any) => {
        this.snackbarService.show(res.message);
        this.isLoading = false;
        this.loadPriceListDate();
      },
      error: (error) => {
        console.error('There was an error deleting all prices', error);
        this.isLoading = false;
      }
    });
  }

  onSelectionChange(selected: any[]) {
    this.selectedRows = selected;
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadPriceListDate();
  }

  importCSV(event: any) {
    this.isLoading = true;
    const file: File = event.target.files[0];
    if (file) {
      const formData: FormData = new FormData();
      formData.append('file', file, file.name);
      formData.append('product_id', this.productId);

      this.apiService.importPricesCSV(formData).subscribe({
        next: (res: any) => {
          this.snackbarService.show('CSV data imported successfully');
          this.loadPriceListDate();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error importing CSV data', error);
          this.snackbarService.show('Error importing CSV data');
          this.isLoading = false;
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }

}
